import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import { shiftService } from '../services/shiftService'
import { toApiError } from '../utils/apiError'
import { CURRENT_SHIFT_QUERY_KEY, PENDING_HANDOFFS_QUERY_KEY, SHIFT_HANDOFFS_QUERY_KEY } from '../constants/handoverConstants'
import type { ShiftHandoffResponse, ShiftResponse } from '../types/shift.types'

interface UpdateNotesVars { shiftId: number; notes: string }
interface CreateHandoffVars { shiftId: number; toSupervisorUserId: number; notes?: string | null }
interface AcknowledgeVars { handoffId: number; propertyId: number }
interface DeclineVars { handoffId: number; reason?: string | null }
interface ForceCloseVars { shiftId: number }

export interface ShiftMutations {
  start: UseMutationResult<ShiftResponse, unknown, { property_id: number; notes?: string | null }>
  updateNotes: UseMutationResult<ShiftResponse, unknown, UpdateNotesVars>
  createHandoff: UseMutationResult<ShiftHandoffResponse, unknown, CreateHandoffVars>
  acknowledge: UseMutationResult<ShiftHandoffResponse, unknown, AcknowledgeVars>
  decline: UseMutationResult<ShiftHandoffResponse, unknown, DeclineVars>
  forceClose: UseMutationResult<ShiftResponse, unknown, ForceCloseVars>
}

export function useShiftMutations(): ShiftMutations {
  const qc = useQueryClient()
  const onError = (err: unknown): void => { toast.error(toApiError(err).message) }
  const invalidateCurrent = (): Promise<void> => qc.invalidateQueries({ queryKey: [CURRENT_SHIFT_QUERY_KEY] }).then(() => undefined)
  const invalidatePending = (): Promise<void> => qc.invalidateQueries({ queryKey: [PENDING_HANDOFFS_QUERY_KEY] }).then(() => undefined)

  const start = useMutation({
    mutationFn: (vars: { property_id: number; notes?: string | null }) => shiftService.start(vars),
    onSuccess: () => { void invalidateCurrent(); toast.success('Shift started.') },
    onError,
  })

  const updateNotes = useMutation({
    mutationFn: ({ shiftId, notes }: UpdateNotesVars) => shiftService.updateNotes(shiftId, { notes }),
    onSuccess: () => { void invalidateCurrent() },
    onError,
  })

  const createHandoff = useMutation({
    mutationFn: ({ shiftId, toSupervisorUserId, notes }: CreateHandoffVars) =>
      shiftService.createHandoff(shiftId, { to_supervisor_user_id: toSupervisorUserId, notes }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: [SHIFT_HANDOFFS_QUERY_KEY] }); toast.success('Handoff sent.') },
    onError,
  })

  const acknowledge = useMutation({
    mutationFn: ({ handoffId }: AcknowledgeVars) => shiftService.acknowledge(handoffId),
    onSuccess: () => { void invalidateCurrent(); void invalidatePending(); toast.success("You're now on shift.") },
    onError,
  })

  const decline = useMutation({
    mutationFn: ({ handoffId, reason }: DeclineVars) => shiftService.decline(handoffId, { reason }),
    onSuccess: () => { void invalidatePending() },
    onError,
  })

  const forceClose = useMutation({
    mutationFn: ({ shiftId }: ForceCloseVars) => shiftService.forceClose(shiftId),
    onSuccess: () => { void invalidateCurrent(); toast.success('Shift force-closed.') },
    onError,
  })

  return { start, updateNotes, createHandoff, acknowledge, decline, forceClose }
}
